import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DtoTest } from "../../../@types/domain-types";
import { encodeAttributesUrl } from "../../../utils/data-encoding";
import { getCurrentUrl } from "../../../utils/urls";
import { PUBLIC_TEST_URL } from "../../Frontend/constants";

type Props = {
  open: boolean;
  onClose: () => void;
  test: DtoTest;
};

type Combo = { key: string; value: string }[];

export function cartesianProduct(
  items: { key: string; values: string[] }[]
): Combo[] {
  const result = items.reduce<Combo[]>(
    (acc, { key, values }) =>
      acc.flatMap(combo => values.map(value => [...combo, { key, value }])),
    [[]]
  );
  return result.length > 500 ? result.slice(0, 501) : result;
}

const ATTRIBUTE_IDS = [1, 2, 3, 4, 5] as const;
const GROUPS = [
  {
    prefix: "specialAttribute",
    titleKey: "tool.preselectedAttributesModal.specialAttributes",
    titleFallback: "Special Attributes",
  },
  {
    prefix: "attribute",
    titleKey: "tool.preselectedAttributesModal.attributes",
    titleFallback: "Attributes",
  },
] as const;
type Prefix = typeof GROUPS[number]["prefix"];

type SelectionKey = `${Prefix}_${number}`;
type SelectedValues = Partial<Record<SelectionKey, string>>;

export default function PreselectedAttributesModal({
  open,
  onClose,
  test,
}: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<SelectedValues>({});
  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const [batchChecked, setBatchChecked] = useState<Record<string, boolean>>({});

  const groupedAttributes = useMemo(
    () =>
      GROUPS.map(({ prefix, titleKey, titleFallback }) => ({
        prefix,
        title: t(titleKey, titleFallback),
        items: ATTRIBUTE_IDS.filter(id => !!test[`${prefix}${id}Label`]).map(
          id => ({
            prefix,
            id,
            label: test[`${prefix}${id}Label`],
            values: (test[`${prefix}${id}Value`] as string[]) ?? [],
          })
        ),
      })).filter(group => group.items.length > 0),
    [t, test]
  );

  const handleChange = useCallback((key: SelectionKey, value: string) => {
    setSelected(prev => ({ ...prev, [key]: value }));
  }, []);

  const attrsObject = useMemo(
    () =>
      Object.fromEntries(
        groupedAttributes
          .flatMap(g => g.items)
          .map(({ prefix, id }) => [
            `${prefix}${id}`,
            selected[`${prefix}_${id}`] ?? "",
          ])
          .filter(([, v]) => v !== "")
      ),
    [groupedAttributes, selected]
  );

  const fullUrl = useMemo(
    () =>
      Object.keys(attrsObject).length > 0
        ? getCurrentUrl(
            `${PUBLIC_TEST_URL}/${test._id}?attrs=${encodeAttributesUrl(
              attrsObject
            )}`
          )
        : "",
    [attrsObject, test._id]
  );

  const batchableItems = useMemo(
    () =>
      groupedAttributes
        .flatMap(g => g.items)
        .filter(item => item.values.length > 0),
    [groupedAttributes]
  );

  const batchRaw = useMemo(() => {
    const batchItems = batchableItems
      .filter(item => batchChecked[`${item.prefix}${item.id}`] !== false)
      .map(item => ({ key: `${item.prefix}${item.id}`, values: item.values }));
    return cartesianProduct(batchItems);
  }, [batchableItems, batchChecked]);

  const batchUrls = useMemo(
    () =>
      batchRaw.slice(0, 500).map(combo => {
        const comboObject = Object.fromEntries(
          combo.map(({ key, value }) => [key, value])
        );
        return getCurrentUrl(
          `${PUBLIC_TEST_URL}/${test._id}?attrs=${encodeAttributesUrl(
            comboObject
          )}`
        );
      }),
    [batchRaw, test._id]
  );

  const batchCapped = batchRaw.length === 501;
  const batchText = batchUrls.join("\n");

  const handleClose = useCallback(() => {
    setSelected({});
    setActiveTab(0);
    setBatchChecked({});
    onClose();
  }, [onClose]);

  const handleCopy = () => {
    if (fullUrl) navigator.clipboard.writeText(fullUrl);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t(
          "tool.addEdit.configurePreselectedAttributes",
          "Configure Preselected Attributes"
        )}
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label={t("tool.preselectedAttributesModal.singleUrl", "Single URL")}
          />
          {/*<Tab label="Batch" />*/}
        </Tabs>

        {activeTab === 0 && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {groupedAttributes.map(({ prefix, title, items }) => (
              <Grid item xs={12} key={prefix} container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {title}
                  </Typography>
                </Grid>
                {items.map(({ id, label, values }) => {
                  const key: SelectionKey = `${prefix}_${id}`;
                  return (
                    <Grid item xs={12} sm={6} key={key}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`${key}-label`}>{label}</InputLabel>
                        <Select
                          labelId={`${key}-label`}
                          value={selected[key] ?? ""}
                          label={label}
                          onChange={(e: SelectChangeEvent) =>
                            handleChange(key, e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {values.map(val => (
                            <MenuItem key={val} value={val}>
                              {val}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                })}
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t("tool.preselectedAttributesModal.fullUrl", "Full URL")}
                value={fullUrl}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleCopy}
                        disabled={!fullUrl}
                        size="small"
                        edge="end"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {batchableItems.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Include attributes:
                </Typography>
                {batchableItems.map(item => {
                  const key = `${item.prefix}${item.id}`;
                  return (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={batchChecked[key] !== false}
                          onChange={e =>
                            setBatchChecked(prev => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                          size="small"
                        />
                      }
                      label={item.label}
                    />
                  );
                })}
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {batchRaw[0]?.length === 0 ? 0 : batchUrls.length} URLs
                generated
              </Typography>
              {batchCapped && (
                <Typography
                  variant="body2"
                  color="warning.main"
                  sx={{ mt: 0.5 }}
                >
                  Showing first 500. More combinations exist.
                </Typography>
              )}
            </Grid>

            {batchRaw[0]?.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  No attributes with values configured.
                </Typography>
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    label="Batch URLs"
                    value={batchText}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => navigator.clipboard.writeText(batchText)}
                  >
                    Copy All
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t("tool.addEdit.close", "Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
